import React, { useEffect } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";

import Landing from "@/pages/Landing";
import UserManual from "@/pages/UserManual";
import Tutorials from "@/pages/Tutorials";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import Profile from "@/pages/Profile";
import Opportunities from "@/pages/Opportunities";
import Compliance from "@/pages/Compliance";
import MyResponses from "@/pages/MyResponses";
import NoticeBoard from "@/pages/NoticeBoard";
import PostOpportunity from "@/pages/PostOpportunity";
import SmmeDatabase from "@/pages/SmmeDatabase";
import Reports from "@/pages/Reports";
import AdminUsers from "@/pages/AdminUsers";
import AdminTraffic from "@/pages/AdminTraffic";
import AdminOrphans from "@/pages/AdminOrphans";

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-[#00427A]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/app" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function AppIndex() {
  const { user } = useAuth();
  if (user?.role === "buyer") return <Navigate to="/app/post" replace />;
  return <Dashboard />;
}

function VisitTracker() {
  const location = useLocation();
  useEffect(() => {
    let vid = localStorage.getItem("assmang_vid");
    if (!vid) {
      vid = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random());
      localStorage.setItem("assmang_vid", vid);
    }
    api.post("/track/visit", { path: location.pathname, visitor_id: vid }).catch(() => {});
  }, [location.pathname]);
  return null;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/user-manual" element={<UserManual />} />
      <Route path="/tutorials" element={<Tutorials />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route path="/app" element={<Protected><AppShell /></Protected>}>
        <Route index element={<AppIndex />} />
        <Route path="opportunities" element={<Opportunities />} />
        <Route path="notice-board" element={<NoticeBoard />} />
        {/* SMME */}
        <Route path="profile" element={<Protected roles={["smme"]}><Profile /></Protected>} />
        <Route path="compliance" element={<Protected roles={["smme"]}><Compliance /></Protected>} />
        <Route path="responses" element={<Protected roles={["smme"]}><MyResponses /></Protected>} />
        {/* Buyer + Admin */}
        <Route path="post" element={<Protected roles={["buyer", "admin"]}><PostOpportunity /></Protected>} />
        <Route path="smme-database" element={<Protected roles={["buyer", "admin"]}><SmmeDatabase /></Protected>} />
        <Route path="reports" element={<Protected roles={["buyer", "admin"]}><Reports /></Protected>} />
        {/* Admin */}
        <Route path="users" element={<Protected roles={["admin"]}><AdminUsers /></Protected>} />
        <Route path="traffic" element={<Protected roles={["admin"]}><AdminTraffic /></Protected>} />
        <Route path="orphans" element={<Protected roles={["admin"]}><AdminOrphans /></Protected>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <VisitTracker />
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
